import Container from "@material-ui/core/Container";
import React, { useState, useEffect, useCallback } from "react";
import {
    Card,
    Grid,
    Box,
    Typography,
    TextField,
    useTheme,
    useMediaQuery,
    Button,
    CircularProgress,
} from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem";
import {colorfff, colorSecondary} from "../../../Constants/theme";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import IconButton from "@material-ui/core/IconButton";
import Collapse from "@material-ui/core/Collapse";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import moment from "moment";
import "moment-timezone";
import Chip from "@material-ui/core/Chip";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import Navigation from "../../Navigation";
import API from "../../../helpers/apiHelpers";
import { RootState, useAppDispatch } from "../../../redux/store";
import { getUserProfile } from "../../../redux/userProfile/userProfile.actions";
import { IWorkDay } from "../../../interfaces";
import Tooltip from "@material-ui/core/Tooltip";

type PendingShift = {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    timezone: string;
};

type DisplayShift = {
    id: string;
    serverId?: number;
    date: string;
    startTime: string;
    endTime: string;
    timezone: string;
    isPending: boolean;
};

const createId = () => `${Date.now()}-${Math.random()}`;

const timezones = [
    { value: "America/New_York", label: "EDT" },
    { value: "America/Chicago", label: "CDT" },
    { value: "America/Denver", label: "MDT" },
    { value: "America/Los_Angeles", label: "PDT" },
    { value: "UTC", label: "UTC" },
];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
    const hours = Math.floor(index / 2);
    const minutes = index % 2 === 0 ? "00" : "30";
    const value = `${String(hours).padStart(2, "0")}:${minutes}`;

    return {
        value,
        label: moment(value, "HH:mm").format("h:mm A"),
    };
});

const mapWorkDayToDisplay = (workday: IWorkDay): DisplayShift => {
    const timezone = moment.tz.guess();
    const begins = moment(workday.workdayBegins).tz(timezone);
    const ends = moment(workday.workdayEnds).tz(timezone);

    return {
        id: String(workday.id),
        serverId: workday.id,
        date: begins.format("YYYY-MM-DD"),
        startTime: begins.format("HH:mm"),
        endTime: ends.format("HH:mm"),
        timezone,
        isPending: false,
    };
};

const mapPendingToDisplay = (shift: PendingShift): DisplayShift => ({
    id: shift.id,
    date: shift.date,
    startTime: shift.startTime,
    endTime: shift.endTime,
    timezone: shift.timezone,
    isPending: true,
});

const toWorkDayPayload = (shift: PendingShift) => ({
    workdayBegins: moment.tz(`${shift.date} ${shift.startTime}`, "YYYY-MM-DD HH:mm", shift.timezone).utc().format(),
    workdayEnds: moment.tz(`${shift.date} ${shift.endTime}`, "YYYY-MM-DD HH:mm", shift.timezone).utc().format(),
});

const VetAvailabilitySection = () => {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const user = useSelector((state: RootState) => state?.user);

    const [availabilityError, setAvailabilityError] = useState("");
    const [upcomingOpen, setUpcomingOpen] = useState(true);
    const [selectedDates, setSelectedDates] = useState<string[]>([]);
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [timezone, setTimezone] = useState( "America/New_York");
    const [savedShifts, setSavedShifts] = useState<IWorkDay[]>([]);
    const [pendingShifts, setPendingShifts] = useState<PendingShift[]>([]);
    const [deletedShiftIds, setDeletedShiftIds] = useState<number[]>([]);
    const [profileId, setProfileId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const today = moment().format("YYYY-MM-DD");

    const formatTime = (time: string) => moment(time, "HH:mm").format("h:mm A");

    const loadWorkdays = useCallback(async (id: number) => {
        const response = await API.getWorkdays(id);
        const workDays = Array.isArray(response)
            ? response
            : response?.workDays ?? [];
        setSavedShifts(workDays);
    }, []);

    useEffect(() => {
    if (selectedDates.length === 0) {
        setStartTime("");
        setEndTime("");
    } else {
        setStartTime((current) => current || "09:00");
        setEndTime((current) => current || "17:00");
    }
    }, [selectedDates.length]);

    useEffect(() => {
        (async () => {
            if (!user?.userId) {
                setIsLoading(false);
                return;
            }

            try {
                const profiles = await dispatch(getUserProfile({userId: user.userId}));
                if (!profiles?.length) {
                    return;
                }

                const id = profiles[0].id;
                setProfileId(id);
                await loadWorkdays(id);
            } catch (error) {
                enqueueSnackbar(error?.message || "Could not load schedule", {variant: "error"});
            } finally {
                setIsLoading(false);
            }
        })();
    }, [user?.userId, dispatch, loadWorkdays, enqueueSnackbar]);

    const upcomingShifts = [
        ...savedShifts
            .filter((workday) => !deletedShiftIds.includes(workday.id))
            .map(mapWorkDayToDisplay),
        ...pendingShifts.map(mapPendingToDisplay),
    ]
        .filter((shift) => shift.date >= today)
        .sort((a, b) => {
            const dateCompare = a.date.localeCompare(b.date);
            if (dateCompare !== 0) {
                return dateCompare;
            }
            return a.startTime.localeCompare(b.startTime);
        });

    const handleAddAvailability = () => {
        if (!selectedDates.length || !startTime || !endTime) {
            return;
        }

        if (startTime >= endTime) {
            setAvailabilityError("End time must be after start time.");
            return;
        }

        const existingShifts = [
            ...savedShifts
                .filter((workday) => !deletedShiftIds.includes(workday.id))
                .map(mapWorkDayToDisplay),
            ...pendingShifts.map(mapPendingToDisplay),
        ];

        const newShifts: PendingShift[] = selectedDates
            .filter((selectedDate) => {
                return !existingShifts.some(
                    (shift) =>
                        shift.date === selectedDate &&
                        shift.startTime === startTime &&
                        shift.endTime === endTime &&
                        shift.timezone === timezone
                );
            })
            .map((selectedDate) => ({
                id: createId(),
                date: selectedDate,
                startTime,
                endTime,
                timezone,
            }));

        if (!newShifts.length) {
            setSelectedDates([]);
            return;
        }

        setPendingShifts([...pendingShifts, ...newShifts]);
        setSelectedDates([]);
        setAvailabilityError("");
        setUpcomingOpen(true);
    };

    const handleDateSelected = (selectedDate: Date | null) => {
        if (!selectedDate) {
            return;
        }

        const formattedDate = moment(selectedDate).format("YYYY-MM-DD");

        if (formattedDate < today) {
            return;
        }

        setSelectedDates((current) =>
            current.includes(formattedDate)
                ? current.filter((date) => date !== formattedDate)
                : [...current, formattedDate]
        );

        setAvailabilityError("");
    };

    const handleDeleteShift = (shift: DisplayShift) => {
        if (shift.isPending) {
            setPendingShifts(pendingShifts.filter((pending) => pending.id !== shift.id));
            return;
        }

        if (!shift.serverId || deletedShiftIds.includes(shift.serverId)) {
            return;
        }

        setDeletedShiftIds([...deletedShiftIds, shift.serverId]);
    };

    const hasUnsavedChanges = pendingShifts.length > 0 || deletedShiftIds.length > 0;

    const handleSave = async () => {
        if (!profileId) {
            enqueueSnackbar("Profile not found", {variant: "error"});
            return;
        }

        if (!hasUnsavedChanges) {
            enqueueSnackbar("No changes to save", {variant: "info"});
            return;
        }

        setIsSaving(true);
        try {
            if (deletedShiftIds.length) {
                const deleteResponse = await API.removeVetScheduleBulk(profileId, {ids: deletedShiftIds});
                if (deleteResponse.status >= 400) {
                    enqueueSnackbar("You cannot remove this shift as you already have an appointment booked during this time.", {variant: "error"});
                    return;
                }
            }

            if (pendingShifts.length) {
                const body = {
                    workDays: pendingShifts.map(toWorkDayPayload),
                };
                const addResponse = await API.addVetSchedule(profileId, body);

                if (addResponse.status !== 200) {
                    enqueueSnackbar("Could not save new shifts. Please try again.", {variant: "error"});
                    return;
                }
            }

            await loadWorkdays(profileId);
            setPendingShifts([]);
            setDeletedShiftIds([]);
            enqueueSnackbar("Schedule saved", {variant: "success"});
        } catch (error) {
            enqueueSnackbar(error?.message || "Could not save schedule", {variant: "error"});
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" padding="3em">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Box style={{ padding: "1.5em" }}>
                <Typography variant="h3" style={{ marginTop: "2em", marginBottom: "0.5em", }}>
                    My Schedule
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ fontWeight: 400, marginBottom: "2em" }}>
                    Please add the dates and times when you are available to receive appointments. You can add multiple dates and times, and you can also remove any availability that you no longer want to offer.
                </Typography>

                <Box display="flex" justifyContent="center" marginBottom={2}>
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                        <DatePicker
                            autoOk
                            disablePast
                            variant="static"
                            value={selectedDates.length ? moment(selectedDates[selectedDates.length - 1]) : null}
                            onChange={handleDateSelected}
                            renderDay={(day, _selectedDate, _dayInCurrentMonth, dayComponent) =>
                                React.cloneElement(dayComponent, {
                                    selected: !!day && selectedDates.includes(moment(day).format("YYYY-MM-DD")),
                                    current: selectedDates.length > 0 && dayComponent.props.current,
                                })
                            }
                        />
                    </MuiPickersUtilsProvider>
                </Box>

                {selectedDates.length > 0 && (
                    <Box display="flex" justifyContent="center" flexWrap="wrap" marginBottom={2}>
                        {selectedDates.map((selectedDate) => (
                            <Box key={selectedDate} marginRight={1} marginBottom={1}>
                                <Chip
                                    label={moment(selectedDate).format("MMM D, YYYY")}
                                    onDelete={() =>
                                        setSelectedDates(selectedDates.filter((date) => date !== selectedDate))
                                    }
                                    color="primary"
                                />
                            </Box>
                        ))}
                    </Box>
                )}

                <Box display="flex" style={{ gap: 16 }} marginBottom={2}>
                    <Box flex={1} width={'45%'}>
                        <TextField
                            fullWidth
                            select
                            variant="outlined"
                            color="primary"
                            label="Start time"
                            value={startTime}
                            disabled={!selectedDates.length}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) => {
                                const nextStartTime = e.target.value;
                                setStartTime(nextStartTime);

                                if (nextStartTime >= endTime) {
                                    const nextEndOption = TIME_OPTIONS.find((option) => option.value > nextStartTime);
                                    if (nextEndOption) {
                                        setEndTime(nextEndOption.value);
                                    }
                                }
                            }}
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: {
                                        style: {
                                            maxHeight: 280,
                                        },
                                    },
                                    getContentAnchorEl: null,
                                    anchorOrigin: {
                                        vertical: "bottom",
                                        horizontal: "left",
                                    },
                                    transformOrigin: {
                                        vertical: "top",
                                        horizontal: "left",
                                    },
                                },
                                displayEmpty: true,
                                renderValue: (selected) =>
                                    !selectedDates.length ? "Select date" : selected ? formatTime(String(selected)) : "Select time",
                            }}
                        >
                            {TIME_OPTIONS.map((option) => (
                                <MenuItem
                                    key={`start-${option.value}`}
                                    value={option.value}
                                    disabled={option.value >= endTime}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <Box flex={1} width={'45%'}>
                        <TextField
                            fullWidth
                            select
                            variant="outlined"
                            color="primary"
                            label="End time"
                            value={endTime}
                            disabled={!selectedDates.length}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) => setEndTime(e.target.value)}
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: {
                                        style: {
                                            maxHeight: 280,
                                        },
                                    },
                                    getContentAnchorEl: null,
                                    anchorOrigin: {
                                        vertical: "bottom",
                                        horizontal: "left",
                                    },
                                    transformOrigin: {
                                        vertical: "top",
                                        horizontal: "left",
                                    },
                                },
                                displayEmpty: true,
                                renderValue: (selected) =>
                                    !selectedDates.length ? "Select date" : selected ? formatTime(String(selected)) : "Select time",
                            }}
                        >
                            {TIME_OPTIONS.map((option) => (
                                <MenuItem
                                    key={`end-${option.value}`}
                                    value={option.value}
                                    disabled={option.value <= startTime}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </Box>

                <Box display="flex" alignItems="center" style={{ gap: 12 }} marginBottom={2}>
                    <Box flex={1}>
                        <TextField
                            fullWidth
                            select
                            variant="outlined"
                            label="Timezone"
                            value={timezone}
                            onChange={(e) => setTimezone(e.target.value as string)}
                        >
                            {timezones.map((tz) => (
                                <MenuItem key={tz.value} value={tz.value}>
                                    {tz.value}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <span>
                        <Button
                            variant="contained"
                            color="secondary"
                            disabled={!selectedDates.length || !startTime || !endTime}
                            onClick={handleAddAvailability}
                            style={selectedDates.length && startTime && endTime ? {
                                backgroundColor: colorSecondary,
                                color: colorfff,
                            } : {}}
                        >
                            Add
                        </Button>
                    </span>
                </Box>

                {availabilityError && (
                    <Typography variant="body2" color="error" style={{ marginTop: 8, fontWeight: 400 }}>
                        {availabilityError}
                    </Typography>
                )}
            </Box>

            <Box padding="1.5em">
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    marginBottom="1em"
                    style={{ cursor: "pointer" }}
                    onClick={() => setUpcomingOpen(!upcomingOpen)}
                >
                    <Typography variant="h6">Upcoming Shifts</Typography>

                    <IconButton size="small">
                        {upcomingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>

                <Collapse in={upcomingOpen}>
                    {upcomingShifts.length === 0 ? (
                        <Box marginTop={1}>
                            <Typography variant="body2" color="textSecondary" style={{ fontWeight: 400 }}>
                                No upcoming availability added yet.
                            </Typography>
                        </Box>
                    ) : (
                        <Box overflow="auto">
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th align="left">Date</th>
                                        <th align="left">Start</th>
                                        <th align="left">End</th>
                                        <th align="left">Timezone</th>
                                        <th align="right">Actions</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {upcomingShifts.map((shift) => (
                                        <tr key={shift.id}>
                                            <td>{moment(shift.date).format("MMM D, YYYY")}</td>
                                            <td>{formatTime(shift.startTime)}</td>
                                            <td>{formatTime(shift.endTime)}</td>
                                            <td>{shift.timezone}</td>

                                            <td align="right">
                                                <IconButton
                                                    size="small"
                                                    color="secondary"
                                                    onClick={() => handleDeleteShift(shift)}
                                                >
                                                    <DeleteOutlineIcon
                                                        fontSize="small"
                                                        style={{ color: "#E57373" }}
                                                    />
                                                </IconButton>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>
                    )}
                </Collapse>
            </Box>
            <Box display="flex" justifyContent="flex-end" padding="1.5em">
                <Button
                    variant="contained"
                    color="secondary"
                    disabled={isSaving || !hasUnsavedChanges}
                    onClick={handleSave}
                    style={!isSaving && hasUnsavedChanges ? {
                        backgroundColor: colorSecondary,
                        color: colorfff,
                    } : {}}
                >
                    Save
                </Button>
            </Box>
        </>
    );
};

const VetSchedule = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    return (
        <Container maxWidth="md" style={{marginBottom: 75}}>
            <Box marginTop="115px">
                <Grid container>
                    {!isMobile &&
						<Grid item md={3} xs={12}>
						    <Navigation />
						</Grid>
                    }
                    <Grid item md={9} xs={12}>
                        <Card variant="outlined">
                            <VetAvailabilitySection />
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default VetSchedule;
